import moment from 'moment';
import { HostPayment } from '../app/Slices/SevenDayApiTypes';

export const capitalizeName = (name?: any): string => {
    return name ? name?.charAt(0).toUpperCase() + name?.slice(1) : '';
};

export const filterItemsBySearch = (items: any, search: any) => {
    if (!search) return items;
    const searchLower = search.toLowerCase();

    const matchesSearch = (value: any, key?: string): boolean => {
        if (key === 'photos') {
            return false;
        }
        if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
        }
        if (typeof value === 'object' && value !== null) {
            return Object.entries(value).some(([k, v]) => matchesSearch(v, k));
        }
        return false;
    };

    return items.filter((item: any) => matchesSearch(item));
};

export const validatEmail = (email: any) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
};

export const validatePhone = (ph: any) => {
    return /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/i.test(
        ph,
    );
};

/** Profile / personal mobile: exactly 10 digits, no other characters. */
export const validatePhoneTenDigits = (ph: unknown) =>
    typeof ph === 'string' && /^\d{10}$/.test(ph);

/** Strip non-digits and cap at 10 for profile phone inputs (API may return spaces). */
export function normalizeProfilePhoneInput(value: unknown): string {
    if (value == null) return '';
    return String(value).replace(/\D/g, '').slice(0, 10);
}

export const validateName = (name: string) => {
    const nameRegex = /^[a-zA-Zà-ÿÀ-ÿ'-\s]+$/;
    return (
        nameRegex.test(name.trim()) &&
        name.trim().length >= 2 &&
        name.trim().length <= 50
    );
};

export const validateLicenceNo = (licenceNo: string) => {
    const minLength = 5;
    const maxLength = 15;
    const licenceRegex = /^[A-Za-z0-9]+$/;

    return (
        licenceNo.length >= minLength &&
        licenceNo.length <= maxLength &&
        licenceRegex.test(licenceNo)
    );
};
export const camelCaseToTitleCase = (input: string): string =>
    input
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (str) => str.toUpperCase());

export const camelCaseToSentenceCase = (input: string): string => {
    const result = input.replace(/([A-Z])/g, ' $1').trim();
    return result?.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
};

export const camelCaseToLowerCase = (input: string): string => {
    const result = input.replace(/([A-Z])/g, ' $1').trim();
    return result?.charAt(0).toLowerCase() + result.slice(1).toLowerCase();
};

export const replaceSpacesWithHyphens = (value: string): string =>
    value.trim().replace(/\s+/g, '-');

export const sum = (numbers: number[] = []): number =>
    numbers.reduce((acc, curr) => acc + curr, 0);

export const formatCurrency = (amount: number, long = true): string => {
    return `$${amount?.toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}${long ? ' AUD' : ''}`;
};

export const formatCurrencyShort = (amount: number): string => {
    return formatCurrency(amount, false);
};

export const getPaymentStatus = (
    payment: HostPayment,
): 'Paid' | 'Unpaid' | '' => {
    return payment.amountDue ? 'Unpaid' : payment.amount ? 'Paid' : '';
};

export const formatTime = (time: string) => {
    return new Date('1970-01-01T' + time + 'Z').toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
    });
};

export const handleHttpError = (error: any) => {
    if (!error?.status) {
        return 'Network error. Please check your connection.';
    }

    switch (error.status) {
        case 400:
            return error?.data?.message || 'Bad Request.';
        case 401:
            return 'Unauthorized. Please log in again.';
        case 403:
            return 'You do not have permission to perform this action.';
        case 404:
            return (
                error?.data?.message || 'The requested resource was not found.'
            );
        case 409:
            return (
                error?.data?.message ||
                'Conflict error. The requested resource is already in use.'
            );
        case 500:
            return 'Internal Server Error. Please try again later.';
        default:
            return 'An unexpected error occurred. Please try again';
    }
};

//find similar experiences
export function kmBetween(
    a: { lat: number; lng: number },
    b: { lat: number; lng: number },
) {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

// utils/pricing.ts
type Charge =
    | {
          chargeType: 'price';
          type: 'fixed';
          unitPrice: number;
          weekendPrice?: { unitPrice?: number };
      }
    | {
          chargeType: 'flexiblePrice';
          dayPrice?: number;
          nightPrice?: number;
          hourPrice?: number;
      }
    | { chargeType: 'fee'; feeId: number; type: 'fixed'; unitPrice: number };

export function getMinDisplayPrice(charges: Charge[] = []) {
    const basePrices = charges
        .filter(
            (c: any) =>
                c.chargeType === 'price' && typeof c.unitPrice === 'number',
        )
        .map((c: any) => c.unitPrice);

    if (basePrices.length > 0) {
        return Math.min(...basePrices);
    }

    const flex = charges.find(
        (c: any) => c.chargeType === 'flexiblePrice',
    ) as any;
    if (flex) {
        const nums = [flex.hourPrice, flex.dayPrice, flex.nightPrice].filter(
            (n) => typeof n === 'number',
        );
        if (nums.length) return Math.min(...nums);
    }
    return undefined;
}

export const getAttendeeLabel = (user: any): string => {
    const hasNames = !!(user?.firstName && user?.lastName);
    return hasNames
        ? `${user.firstName} ${user.lastName} (${user.email})`
        : user.email;
};

export const groupCleaningFees = (charges: any[] = []) => {
    const safeCharges = Array.isArray(charges) ? charges : [];

    const cleaningFeeTotal = sum(
        safeCharges
            .filter((charge: any) => charge.chargeType === 'cleaningFee')
            .map((charge: any) => charge.total),
    );

    const cleaningFees = safeCharges.filter(
        (charge: any) =>
            charge.chargeType === 'cleaningFee' && charge.total !== 0,
    );

    const isFlatCleaningFee =
        cleaningFees.length === 1 && cleaningFees[0]?.type === 'fixed';

    const result = [
        ...safeCharges.filter(
            (charge: any) =>
                charge.chargeType !== 'cleaningFee' && charge.total !== 0,
        ),
    ];

    if (cleaningFeeTotal > 0) {
        result.push({
            chargeType: 'cleaningFee',
            total: cleaningFeeTotal,
            name: 'Cleaning Fee',
            cleaningFeeType: isFlatCleaningFee ? 'flat' : 'flexible',
            details: safeCharges.filter(
                (charge: any) => charge.chargeType === 'cleaningFee',
            ),
        });
    }

    return result;
};

export const joinWithAnd = (arr: string[]): string => {
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return arr.join(' and ');
    return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
};

export const addHoursToTime = (time: string, hoursToAdd: number): string => {
    const [hours, minutes] = time.split(':').map(Number);

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    date.setHours(date.getHours() + hoursToAdd);

    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');

    return `${hh}:${mm}`;
};
export const getMinTimeFromArray = (times: string[]): string => {
    return times.reduce((min, curr) => (curr < min ? curr : min));
};

export const dayToNumber: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

export const convertTimeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export async function generateThumbnailFromVideo(
    videoUrl: string,
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;

        video.addEventListener('loadeddata', () => {
            video.currentTime = 0.5;
        });

        video.addEventListener('seeked', () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject('Failed to create blob');
                }, 'image/jpeg');
            } else {
                reject('Could not get canvas context');
            }
        });

        video.onerror = (e) => reject(`Error loading video: ${e}`);
    });
}

export function formatServiceItemsList(items: string[]): string {
    if (items.length <= 1) {
        return items.join('');
    }

    if (items.length === 2) {
        return `${items[0]} or ${items[1]}`;
    }

    return `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`;
}

export const getDateArray = (
    startDate?: string,
    endDate?: string,
): string[] => {
    const dateArray: string[] = [];
    if (!startDate || !endDate) return dateArray;
    const start = moment(startDate, 'YYYY-MM-DD');
    const end = moment(endDate, 'YYYY-MM-DD');
    for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
        dateArray.push(d.format('YYYY-MM-DD'));
    }
    return dateArray;
};
